/**
 * Akıllı Arama — Daireler Listesi için katı (AND) filtre.
 *
 * Müşteri sunumlarındaki Akıllı Arama sonuçları puanlayıp öneri sıralar; burada
 * ise aynı Türkçe ayrıştırıcının çıkardığı kriterler listeyi daraltan filtre olarak
 * uygulanır. Saf fonksiyonlar; veri istemcide kalır, yapay zekaya gönderilmez.
 */

import {
  AramaKriterleri, KriterEtiketi, kriterEtiketleri, parseAramaKriterleri,
  trFold, trIcerir, zenginlestirKriterler,
} from './daireAramaParser';

export interface AkilliFiltreDaire {
  daireNo: string;
  daireDurumu: string;
  blokID: number;
  listeFiyati?: number | null;
  netM2?: number | null;
  brutM2?: number | null;
  proje?: { projeAdi?: string; projeDurumu?: string; sehir?: string | null } | null;
  blok?: { blokAdi?: string } | null;
  kat?: { katAdi?: string; sira?: number | null } | null;
  cephe?: { cepheAdi?: string } | null;
  manzara?: { manzaraAdi?: string } | null;
  daireTipi?: { tip?: string } | null;
}

export interface DaireAkilliKriter extends AramaKriterleri {
  blok: string | null;
  /** Metindeki harf+rakam içeren parçalar ("A-3", "B12") — daire no adayları */
  daireNoAdaylari: string[];
}

// Yapısal alanları niteleyen sözcükler; tek başına filtre anlamı taşımaz.
const YAPISAL_SOZCUKLER = new Set([
  'blok', 'bloklu', 'cephe', 'cepheli', 'manzara', 'manzarali', 'manzarasi',
  'kat', 'katli', 'bakan', 'goren', 'oda', 'odali',
]);

const odaTipi = (tip: string | null | undefined): string | null => {
  if (!tip) return null;
  const m = trFold(tip).match(/(\d{1,2})\s*\+\s*(\d{1,2})/);
  return m ? `${m[1]}+${m[2]}` : null;
};

const metinHavuzu = (d: AkilliFiltreDaire): string =>
  trFold([
    d.daireNo, d.daireDurumu, d.proje?.projeAdi, d.proje?.sehir, d.blok?.blokAdi,
    d.kat?.katAdi, d.cephe?.cepheAdi, d.manzara?.manzaraAdi, d.daireTipi?.tip,
  ].filter(Boolean).join(' '));

/** Metni kriterlere çevirir; "A blok" gibi bu sayfaya özgü ifadeleri de yakalar. */
export const metindenKriter = (metin: string, projeAdlari: string[]): DaireAkilliKriter => {
  const k = zenginlestirKriterler(parseAramaKriterleri(metin), metin, projeAdlari);
  const f = trFold(metin);
  const blokEsleme = f.match(/(?:^|\s)([a-z0-9]{1,3})\s*-?\s*blok(?:\s|$)/);
  const daireNoAdaylari = f.split(' ').filter(p => /[a-z]/.test(p) && /\d/.test(p) && !/\d\+\d/.test(p));
  return {
    ...k,
    blok: blokEsleme ? blokEsleme[1] : null,
    daireNoAdaylari,
    serbestAnahtarKelimeler: k.serbestAnahtarKelimeler.filter(s => !YAPISAL_SOZCUKLER.has(s) && !daireNoAdaylari.includes(s)),
  };
};

/** Yalnızca veride birebir karşılığı olan daire no adayları filtre olur. */
export const etkinDaireNolari = (k: DaireAkilliKriter, tumDaireler: AkilliFiltreDaire[]): string[] => {
  if (k.daireNoAdaylari.length === 0) return [];
  const mevcut = new Set(tumDaireler.map(d => trFold(d.daireNo)));
  return k.daireNoAdaylari.filter(a => mevcut.has(a));
};

/** Veride hiç geçmeyen serbest sözcükler gürültü sayılır; listeyi boşaltmaması için elenir. */
export const etkinAnahtarKelimeler = (k: DaireAkilliKriter, tumDaireler: AkilliFiltreDaire[]): string[] => {
  if (k.serbestAnahtarKelimeler.length === 0) return [];
  const havuzlar = tumDaireler.map(metinHavuzu);
  return k.serbestAnahtarKelimeler.filter(s => {
    const f = trFold(s);
    return havuzlar.some(h => h.includes(f));
  });
};

const katSirasi = (d: AkilliFiltreDaire, yedek: (katAdi: string) => number): number =>
  d.kat?.sira ?? yedek(d.kat?.katAdi || '');

/** Blok başına kat sıraları — "son kat", "ara kat" gibi göreli tercihler için. */
export const blokKatSiralari = (
  tumDaireler: AkilliFiltreDaire[],
  yedek: (katAdi: string) => number,
): Map<number, number[]> => {
  const map = new Map<number, Set<number>>();
  tumDaireler.forEach(d => {
    if (!map.has(d.blokID)) map.set(d.blokID, new Set());
    map.get(d.blokID)!.add(katSirasi(d, yedek));
  });
  const sonuc = new Map<number, number[]>();
  map.forEach((s, blokID) => sonuc.set(blokID, Array.from(s).sort((a, b) => a - b)));
  return sonuc;
};

const katUyar = (
  d: AkilliFiltreDaire,
  tercih: string,
  siralar: Map<number, number[]>,
  yedek: (katAdi: string) => number,
): boolean => {
  const katAdi = trFold(d.kat?.katAdi || '');
  const numara = tercih.match(/^(\d{1,2})\./);
  if (numara) {
    const m = katAdi.match(/\d{1,2}/);
    return !!m && m[0] === numara[1];
  }
  const f = trFold(tercih);
  if (f === 'giris') return katAdi.includes('zemin') || katAdi.includes('giris');
  if (f === 'bahce') return katAdi.includes('bahce');

  const liste = siralar.get(d.blokID) || [];
  if (liste.length === 0) return false;
  const sira = katSirasi(d, yedek);
  const enAlt = liste[0];
  const enUst = liste[liste.length - 1];
  if (f === 'son kat') return sira === enUst;
  if (f === 'ara kat') return sira !== enAlt && sira !== enUst;
  if (f === 'yuksek kat') return sira >= liste[Math.floor(liste.length / 2)];
  return trIcerir(d.kat?.katAdi, tercih);
};

const aralikta = (deger: number | null | undefined, min: number | null, max: number | null): boolean => {
  if (deger === null || deger === undefined || deger <= 0) return false;
  if (min !== null && deger < min) return false;
  if (max !== null && deger > max) return false;
  return true;
};

export const akilliFiltreUyar = (
  d: AkilliFiltreDaire,
  k: DaireAkilliKriter,
  kelimeler: string[],
  daireNolar: string[],
  siralar: Map<number, number[]>,
  yedek: (katAdi: string) => number,
): boolean => {
  if (daireNolar.length > 0 && !daireNolar.includes(trFold(d.daireNo))) return false;
  if ((k.minFiyat !== null || k.maxFiyat !== null) && !aralikta(d.listeFiyati, k.minFiyat, k.maxFiyat)) return false;

  if (k.minM2 !== null || k.maxM2 !== null) {
    const m2 = k.m2Turu === 'brut' ? (d.brutM2 ?? d.netM2) : (d.netM2 ?? d.brutM2);
    if (!aralikta(m2, k.minM2, k.maxM2)) return false;
  }

  if (k.odaTipleri.length > 0) {
    const tip = odaTipi(d.daireTipi?.tip);
    if (!tip || !k.odaTipleri.includes(tip)) return false;
  }

  if (k.projeAdi && !trIcerir(d.proje?.projeAdi, k.projeAdi)) return false;
  if (k.sehir && !trIcerir(d.proje?.sehir, k.sehir)) return false;

  if (k.yeniProje !== null) {
    const durum = d.proje?.projeDurumu || '';
    const devamEden = durum === 'Gelecek' || durum === 'Devam Ediyor';
    if (k.yeniProje !== devamEden) return false;
  }

  if (k.cephe) {
    const cephe = trFold(d.cephe?.cepheAdi || '').replace(/\s/g, '');
    if (!cephe.includes(k.cephe)) return false;
  }
  if (k.manzara && !trIcerir(d.manzara?.manzaraAdi, k.manzara)) return false;
  if (k.katTercihi && !katUyar(d, k.katTercihi, siralar, yedek)) return false;

  if (k.blok) {
    const b = trFold(d.blok?.blokAdi || '');
    const eslesir = b === k.blok || b.startsWith(`${k.blok} `) || b.includes(`${k.blok} blok`) || b.includes(`${k.blok}blok`);
    if (!eslesir) return false;
  }

  if (kelimeler.length > 0) {
    const havuz = metinHavuzu(d);
    if (!kelimeler.every(s => havuz.includes(trFold(s)))) return false;
  }

  return true;
};

export interface AkilliFiltreCipi {
  anahtar: string;
  etiket: string;
}

// Ayrıştırıcı cephe/manzarayı Türkçe karaktersiz döndürür; çipte doğru yazımla gösterilir.
const GORUNEN_AD: Record<string, string> = {
  guney: 'Güney', kuzey: 'Kuzey', dogu: 'Doğu', bati: 'Batı',
  guneybati: 'Güneybatı', guneydogu: 'Güneydoğu', kuzeybati: 'Kuzeybatı', kuzeydogu: 'Kuzeydoğu',
  deniz: 'Deniz', orman: 'Orman', sehir: 'Şehir', gol: 'Göl', bogaz: 'Boğaz',
  park: 'Park', doga: 'Doğa', havuz: 'Havuz',
};

/** Filtrede gerçekten etkili olan kriterlerin çipleri (satılık/konut gibi bu listede anlamsız olanlar hariç). */
export const akilliFiltreCipleri = (k: DaireAkilliKriter, kelimeler: string[], daireNolar: string[]): AkilliFiltreCipi[] => {
  const haric: KriterEtiketi['alan'][] = ['islemTuru', 'gayrimenkulTuru'];
  const cipler: AkilliFiltreCipi[] = daireNolar.map(n => ({ anahtar: `daire:${n}`, etiket: `Daire ${n.toLocaleUpperCase('tr-TR')}` }));
  kriterEtiketleri(k)
    .filter(e => !haric.includes(e.alan))
    .forEach(e => {
      let etiket = e.etiket;
      if (e.alan === 'cephe' && k.cephe && GORUNEN_AD[k.cephe]) etiket = `${GORUNEN_AD[k.cephe]} cephe`;
      if (e.alan === 'manzara' && k.manzara && GORUNEN_AD[k.manzara]) etiket = `${GORUNEN_AD[k.manzara]} manzara`;
      cipler.push({ anahtar: e.alan, etiket });
    });
  if (k.blok) cipler.push({ anahtar: 'blok', etiket: `${k.blok.toLocaleUpperCase('tr-TR')} Blok` });
  kelimeler.forEach(s => cipler.push({ anahtar: `kelime:${s}`, etiket: `"${s}"` }));
  return cipler;
};
